export interface AgentConversationDto {
  id: string;
  project_id: number;
  creator_id: string | null;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface AgentConversationListDto {
  items: AgentConversationDto[];
  next_cursor: string | null;
}

export interface ArtifactMetadataDto {
  checksum: string;
  size_bytes: number | null;
  status: "pending" | "uploaded";
  filename: string | null;
  media_type: string | null;
  description: string | null;
  conversation_id: string | null;
}

export interface ArtifactReadUrlDto extends ArtifactMetadataDto {
  url: string;
}

/**
 * Supported media types
 */
export enum MediaType {
  Metrics = "application/vnd.skore.metrics.v1",
  Comparison = "application/vnd.skore.comparison.v1+json",
  Vega = "application/vnd.vega.v5+json",
  Plotly = "application/vnd.plotly.v1+json",
  TableReport = "application/vnd.skrub.table-report.v1+json",
  DataFrame = "application/vnd.dataframe",
}

/**
 * Report URN prefixes
 */
export enum ReportUrnPrefix {
  Estimator = "skore:report:estimator",
  CrossValidation = "skore:report:cross-validation",
  Comparison = "skore:report:comparison",
}

export function extractIdFromUrn(urn: string) {
  return parseInt(urn.split(":").pop() ?? "-1");
}

/** Assignable workspace membership roles (identity `WorkspaceRole`). */
export enum WorkspaceMemberRole {
  Owner = "owner",
  Contributor = "contributor",
  Reader = "reader",
}

/** All roles in display order (e.g. role dropdowns). */
export const WORKSPACE_MEMBER_ROLES: readonly WorkspaceMemberRole[] = [
  WorkspaceMemberRole.Owner,
  WorkspaceMemberRole.Contributor,
  WorkspaceMemberRole.Reader,
];

/**
 * A workspace member
 */
export interface WorkspaceMemberDto {
  workspace_id: number;
  user_id: string;
  role: WorkspaceMemberRole;
  invited_by?: string;
  created_at: string;
}

/**
 * A workspace
 */
export interface WorkspaceDto {
  id: number;
  public_id: string;
  created_at: string;
  updated_at: string;
  members?: WorkspaceMemberDto[];
}

/**
 * Result of checking whether a workspace public id (slug) is free.
 * When available, `suggested_slug` is always null; when taken, it is always a string.
 */
export type WorkspacePublicIdAvailabilityDto =
  | { available: true; suggested_slug: null }
  | { available: false; suggested_slug: string };

/**
 * An invitation to a workspace
 */
export interface InvitationDto {
  email: string;
  role: WorkspaceMemberRole;
}

/** Response from POST .../invitation-link (shareable join URL). */
export interface WorkspaceInvitationLinkDto {
  url: string;
  expires_at: string | null;
}

/** Unused, unexpired workspace invitation (email or shareable link). */
export interface PendingWorkspaceInvitationDto {
  id: number;
  invitation_email: string | null;
  role: WorkspaceMemberRole;
  expires_at: string | null;
  created_at: string;
  url?: string;
}

/** Optional server-side features enabled on this deployment. */
export interface FeaturesDto {
  smtp_email_enabled: boolean;
  project_chat_enabled: boolean;
}

/**
 * LLM provider a workspace can select for the agent.
 *
 * Providers are deployment-backed options IT has configured with credentials
 * and model tiers. The workspace only chooses which provider + model to use.
 */
export type AgentProvider = "anthropic" | "bedrock" | "openrouter";

/** One difficulty level. A null provider or model follows the default. */
export interface AgentTierOverrideDto {
  provider?: AgentProvider | null;
  model?: string | null;
}

/** Per-tier departures from the default provider and model. Null when none differ. */
export interface AgentTierOverridesDto {
  big?: AgentTierOverrideDto | null;
  normal?: AgentTierOverrideDto | null;
  small?: AgentTierOverrideDto | null;
}

/**
 * A workspace's single agent provider selection.
 *
 * `default_provider` is `null` when no selection has been made yet. A selection
 * carries no secrets (credentials live in the deployment config).
 */
export interface AgentProviderSelectionDto {
  workspace_id: number;
  /** The selected provider, or `null` when the agent is not configured. */
  default_provider: AgentProvider | null;
  /** Default model. A tier uses it when that tier's model is null. */
  default_model: string | null;
  tier_overrides?: AgentTierOverridesDto | null;
}

/**
 * The workspace's provider selection plus UI helper metadata.
 *
 * `available_models` carries the per-provider selectable lists (besides "Auto");
 * it only contains the providers IT has enabled on the deployment.
 */
export interface AgentProviderSelectionResponseDto {
  selection: AgentProviderSelectionDto | null;
  available_models: Record<string, string[]>;
}

/**
 * Payload to set a workspace's agent provider selection.
 *
 * No credentials: the workspace only names a provider and a model.
 */
export interface AgentProviderSelectionUpdateDto {
  default_provider: AgentProvider;
  /** Default model id. Required. */
  default_model?: string | null;
  /** Omit to clear every tier override. */
  tier_overrides?: AgentTierOverridesDto | null;
}

/** Write payload. A tier that matches the default is omitted. */
export function agentProviderSelectionUpdate(
  defaultProvider: AgentProvider,
  defaultModel: string,
  tiers: Record<"big" | "normal" | "small", { provider: AgentProvider; model: string }>
): AgentProviderSelectionUpdateDto {
  const tierOverrides: AgentTierOverridesDto = {};
  for (const key of ["big", "normal", "small"] as const) {
    const tier = tiers[key];
    if (tier.provider !== defaultProvider) {
      tierOverrides[key] = { provider: tier.provider, model: tier.model };
    } else if (tier.model) {
      tierOverrides[key] = { model: tier.model };
    }
  }
  return {
    default_provider: defaultProvider,
    default_model: defaultModel,
    ...(Object.keys(tierOverrides).length > 0 ? { tier_overrides: tierOverrides } : {}),
  };
}

/**
 * One workspace membership on the identity user profile (`UserDto.workspace_memberships`).
 */
export interface WorkspaceMembershipInfoDto {
  workspace_id: number;
  public_id: string;
  role: WorkspaceMemberRole;
  permissions: string[];
}

export interface UserDto {
  id: string;
  email_verified: boolean;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  workspace_memberships: WorkspaceMembershipInfoDto[];
}

export function getUserFriendlyName(user: UserDto) {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.email;
}

/**
 * A project
 */
export interface ProjectDto {
  id: number;
  public_id: string;
  name: string;
  workspace_id: number;
  creator_id: string;
  estimator_report_count: number;
  cross_validation_report_count: number;
  presentation_count: number;
  comparison_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * A project item is a single item in the project.
 *
 * It's contributed by a user python side.
 */
export interface ProjectItemDto {
  name: string;
  media_type: string;
  value: any;
  updated_at: string;
  created_at: string;
  note: string | null;
  version: number;
}

export enum ReportType {
  EstimatorReport,
  CrossValidationReport,
}

export interface MetricDto {
  name: string;
  verbose_name: string;
  data_source: string | null;
  value: number;
  greater_is_better: boolean | null;
  /** Class label for per-class classification metrics; `null` otherwise. */
  label: string | null;
  /** Output index for multioutput regression metrics; `null` otherwise. */
  output: number | null;
  /** Averaging mode for metrics aggregated across labels/outputs; `null` otherwise. */
  average: string | null;
}

/** Comparison operator for project goal constraints. */
export enum ComparisonOperator {
  Lt = "lt",
  Gt = "gt",
  Lte = "lte",
  Gte = "gte",
}

/** Metric type available for goals on a project dataset. */
export interface MetricTypeDto {
  id: number;
  name: string;
  verbose_name: string;
  greater_is_better: boolean | null;
  /** Class label for per-class classification metric variants; `null` otherwise. */
  label?: string | null;
  /** Output index for multioutput regression metric variants; `null` otherwise. */
  output?: number | null;
  /** Averaging mode for aggregated metric variants; `null` otherwise. */
  average?: string | null;
}

/** A metric constraint goal for a project. */
export interface ProjectGoalDto {
  id: number;
  metric_type_id: number;
  metric_name: string;
  metric_verbose_name: string;
  constraint_operator: ComparisonOperator;
  constraint_threshold: number | null;
  constraint_threshold_source: string | null;
  is_main: boolean;
  /** Class label this goal targets; `null` for the aggregate metric. */
  label: string | null;
  /** Output index this goal targets; `null` for the aggregate metric. */
  output: number | null;
  /** Averaging mode this goal targets; `null` for the aggregate metric. */
  average: string | null;
  created_at: string;
  updated_at: string;
}

/** Independent main-metric bucket (mirrors backend ``MainScopeBucket``). */
export type MainScopeBucket = "global" | "label" | "output" | "average";

/** Dropdown scope for which a goal is the main metric. */
export interface ProjectGoalMainScopeDto {
  goal_id: number;
  bucket: MainScopeBucket;
  label: string | null;
  output: number | null;
  average: string | null;
}

/** List goals response including independent main-scope pointers. */
export interface ProjectGoalsListDto {
  goals: ProjectGoalDto[];
  main_scopes: ProjectGoalMainScopeDto[];
}

/** Variant dimensions identifying a Goal settings / report main-metric bucket. */
export interface MetricVariantScopeDto {
  bucket?: MainScopeBucket | null;
  label?: string | null;
  output?: number | null;
  average?: string | null;
}

export interface CreateProjectGoalParametersDto {
  metric_type_id: number;
  constraint_operator: ComparisonOperator;
  constraint_threshold?: number | null;
  constraint_threshold_source?: string | null;
  is_main?: boolean;
  label?: string | null;
  output?: number | null;
  average?: string | null;
  main_scope?: MetricVariantScopeDto | null;
}

export interface UpdateProjectGoalParametersDto {
  constraint_operator: ComparisonOperator;
  constraint_threshold?: number | null;
  constraint_threshold_source?: string | null;
  is_main: boolean;
  main_scope?: MetricVariantScopeDto | null;
}

export interface CreateProjectGoalResponseDto {
  id: number;
}

export interface ProjectGoalMeetingReportsCountDto {
  meeting_count: number;
  total_count: number;
}

export interface EstimatorReportSummaryDto {
  id: number;
  urn: string;
  project_id: number;
  creator_id: string;
  key: string;
  ml_task: string;
  estimator_class_name: string;
  metrics: MetricDto[];
  dataset_fingerprint: string;
  dataset_verbose_name: string | null;
  updated_at: string;
  created_at: string;
}

export interface CrossValidationReportSummaryDto extends EstimatorReportSummaryDto {
  splitter_type: string;
}

export type ReportSummaryType = "estimator" | "cross-validation";

export interface ReportSummaryDto {
  id: number;
  urn: string;
  key: string;
  type: ReportSummaryType;
  ml_task: string;
  estimator_class_name: string;
  dataset_fingerprint: string;
  dataset_verbose_name: string | null;
  creator_id: string | null;
  metrics: MetricDto[];
  updated_at: string;
  created_at: string;
}

export interface ListReportsResponseDto {
  items: ReportSummaryDto[];
  next_cursor: number | null;
}

/**
 * ReportMedia
 */
export interface ReportMediaDto {
  checksum: string;
  data_source: string | null;
  size_bytes: number;
  name: string;
  verbose_name: string;
  category: string;
  method: string | null;
  content_type: string;
  parameters: Record<string, unknown> | null;
  presigned_url: string;
}

/**
 * An EstimatorReport DTO.
 */
export interface EstimatorReportDto extends EstimatorReportSummaryDto {
  medias: ReportMediaDto[];
  parents_urns: string[];
}

export interface CrossValidationSplittingStrategyDto {
  splitter: {
    type: string;
    n_splits: number;
    n_repeats: number | null;
    shuffle: boolean;
    random_state: string | null;
  };
  splits: (number[] | null)[];
  train_target_distributions: number[][];
  test_target_distributions: number[][];
  train_target_distributions_sample_counts: number[];
  test_target_distributions_sample_counts: number[];
}

export function crossValidationSplitsHaveAssignments(
  splits: CrossValidationSplittingStrategyDto["splits"]
): boolean {
  return splits.length > 0 && splits.every((split) => split !== null);
}

export function getCrossValidationSplitCount(
  splittingStrategy: CrossValidationSplittingStrategyDto
): number {
  const nRepeats = splittingStrategy.splitter.n_repeats;
  const repeats = nRepeats != null && nRepeats > 1 ? nRepeats : 1;
  return splittingStrategy.splits.length * repeats;
}

/** Count of splits that can actually be opened (never more than cached estimators). */
export function getNavigableSplitCount(
  report: Pick<CrossValidationReportDto, "estimators"> &
    Partial<Pick<CrossValidationReportDto, "splitting_strategy">>
): number {
  const n = report.estimators.length;
  if (!report.splitting_strategy) return n;
  return Math.min(getCrossValidationSplitCount(report.splitting_strategy), n);
}

/**
 * A cross validation report DTO.
 */
export interface CrossValidationReportDto extends EstimatorReportSummaryDto {
  medias: ReportMediaDto[];
  dataset_size: number;
  groups: number[] | null;
  class_names: string[] | null;
  target_ranges_normalized: [number, number][] | null;
  target_names: string[] | null;
  estimators: number[];
  splitting_strategy: CrossValidationSplittingStrategyDto;
}

/**
 * Get report's name from a report dto
 * @param report a report dto
 * @returns the report's name
 */
export function getReportName(report: Pick<EstimatorReportSummaryDto, "id" | "key">) {
  return report.key ? report.key : `xp #${report.id}`;
}

/* Check if a report is a cross-validation report.
 *
 * @param report Estimator or cross-validation report summary/full DTO
 */
export function isCrossValidationReport(report: EstimatorReportSummaryDto): boolean {
  return report.urn.startsWith(`${ReportUrnPrefix.CrossValidation}:`);
}

/**
 * Check if a report is a child of a cross validation report
 * @param report a estimator report dto
 * @returns true if the report is a child of a cross validation report, false otherwise
 */
export function isChildOfCrossValidationReport(
  report: EstimatorReportDto | CrossValidationReportDto
) {
  if (!("parents_urns" in report)) {
    return false;
  }
  return report.parents_urns.some((urn) => urn.startsWith(ReportUrnPrefix.CrossValidation));
}

/**
 * Report comparison DTO
 *
 * The result of a set of reports comparison.
 */
export interface EstimatorReportComparisonDto {
  id: number;
  project_id: number;
  creator_id: string;
  name: string;
  compared_reports: number[];
  best_trade_offs: number[];
  best_trade_offs_explanations: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export interface ApiLivenessDto {
  version: string;
  hash_commit: string | null;
}

export interface ApiHealthDto extends ApiLivenessDto {
  redis: boolean;
  redis_response_time_ms: number;
  database: boolean;
  database_response_time_ms: number;
}

export interface UserAPIKeyDto {
  id: number;
  user_id: string;
  workspace_id: number;
  name: string;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
}

export interface DataframeDto {
  index: number[];
  columns: string[];
  data: (string | number | null)[][];
  index_names?: (string | null)[];
  column_names?: (string | null)[];
}

export type CheckSection = "issue" | "tip" | "passed" | "not_applicable" | "skipped" | "ignored";

export interface ChecksSummaryRowDto {
  code: string;
  title: string;
  section: CheckSection;
  explanation: string | Record<string, string> | null;
  documentation_url: string | null;
}
